/**
 * CookieConsent — Cookie Banner Overlay (Audi CI, audi.de spec)
 *
 * Accepts an optional `texts` prop (from market config's `cookies` field).
 * All strings fall back to hardcoded EN defaults so the banner always renders
 * correctly even when config is still loading.
 *
 * Layout: backdrop position:absolute covers full viewport (incl. rounded corners).
 * Sheet: position:absolute, bottom:0, slides up via double-rAF.
 * Buttons: pinned sticky at bottom; category area scrolls independently.
 * Toggles: knob left:0 explicit, no toggle for locked (Technische Cookies).
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppStore } from '../../store/app.store';
import type { CookieTexts } from '@ims/shared';

const SLIDE_MS = 400;

/** Hardcoded EN fallback strings — used when market config is not yet loaded. */
const EN_FALLBACKS: Required<CookieTexts> = {
  detailedTitle:          'Use of Cookies and Web Technologies',
  detailedDescription:    'We use our own services and third-party services on our website that use cookies and similar web technologies to improve our website, analyse traffic and usage, and display content tailored to your interests, including personalised advertising. By clicking \u201cAccept all\u201d you consent to the use of all services. You can withdraw your consent at any time in the cookie settings. Further information and specific details on the use of your personal data can be found in our Cookie Policy, Privacy Notice and Legal Notice.',
  acceptAll:              'Accept all',
  rejectOptional:         'Reject optional cookies',
  necessaryLabel:         'Technical cookies',
  necessaryDescription:   'These are required to provide you with basic functions while using the website. Further information can be found in our Cookie Policy.',
  performanceLabel:       'Analytics and statistics',
  performanceDescription: 'Performance cookies collect information about how our website is used (e.g. number of visits, time on page). These cookies are used to optimise the website.',
  marketingLabel:         'Marketing',
  marketingDescription:   'Marketing cookies are used to offer users more relevant content tailored to their interests. They are also used to limit how often an advertisement is shown and to measure and control the effectiveness of advertising campaigns.',
};

export interface CookieConsentProps {
  /** Optional cookie texts from market config. Missing keys fall back to EN defaults. */
  texts?: CookieTexts;
}

/* Token values (from index.css) */
const T = {
  sheetBg:         '#2a3340',
  text:            '#fcfcfd',
  textSecondary:   '#c3c4c8',
  divider:         'rgba(255,255,255,0.10)',
  btnLine:         'rgba(255,255,255,0.08)',

  primaryBg:       '#657081',
  primaryBorder:   '#657081',
  primaryHoverBg:  '#2c343f',
  primaryHoverBorder: '#4d545e',

  secondaryBg:     '#181d25',
  secondaryBorder: '#36404e',
  secondaryHoverBg:'#2a3340',

  toggleOffBg:     '#181d25',
  toggleOnBg:      '#89ff89',
  toggleBorder:    '#181d25',
  knobOff:         '#ffffff',
  knobOn:          '#181d25',
} as const;

export function CookieConsent({ texts }: CookieConsentProps = {}) {
  // Merge provided texts with DE fallback — per-key, so partial market overrides work.
  const t: Required<CookieTexts> = { ...EN_FALLBACKS, ...texts };
  const giveConsent = useAppStore((s) => s.giveConsent);
  const [performance, setPerformance] = useState(false);
  const [marketing,   setMarketing]   = useState(false);
  const [sheetIn,     setSheetIn]     = useState(false);
  const [backdropIn,  setBackdropIn]  = useState(true);
  const dismissingRef = useRef(false);

  // Lock scroll on html + body while user must choose
  useEffect(() => {
    const pb = document.body.style.overflow;
    const ph = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = pb;
      document.documentElement.style.overflow = ph;
    };
  }, []);

  // Slide sheet in — double-rAF ensures translateY(100%) is painted first
  useEffect(() => {
    const r1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => setSheetIn(true));
    });
    return () => cancelAnimationFrame(r1);
  }, []);

  function dismiss(perf: boolean, mkt: boolean) {
    if (dismissingRef.current) return;
    dismissingRef.current = true;
    setSheetIn(false);
    setBackdropIn(false);
    setTimeout(() => giveConsent(perf, mkt), SLIDE_MS);
  }

  return createPortal(
    /* Outer: fills viewport, contains absolute backdrop + absolute sheet */
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>

      {/* Backdrop — absolute, covers ENTIRE viewport including behind sheet rounded corners */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          opacity: backdropIn ? 1 : 0,
          transition: `opacity ${SLIDE_MS}ms ease-in-out`,
        }}
      />

      {/* Sheet — slides up from viewport bottom */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t.detailedTitle}
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: T.sheetBg,
          borderRadius: '20px 20px 0 0',
          transform: sheetIn ? 'translateY(0)' : 'translateY(100%)',
          transition: `transform ${SLIDE_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`,
          maxHeight: '85dvh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain', padding: '28px 24px 0' }}>

          {/* Title */}
          <h2 style={{ margin: '0 0 16px', color: T.text, fontWeight: 600, fontSize: '1.375rem', lineHeight: 1.25, fontFamily: 'inherit' }}>
            {t.detailedTitle}
          </h2>

          {/* Description */}
          <p style={{ margin: '0 0 24px', color: T.text, fontSize: '0.875rem', lineHeight: 1.6, fontFamily: 'inherit' }}>
            {t.detailedDescription}
          </p>

          {/* ── Categories ───────────────────────────────────────────────── */}

          {/* Technische Cookies — locked, NO toggle shown */}
          <CategoryRow
            label={t.necessaryLabel}
            description={t.necessaryDescription}
          />
          <Divider />

          {/* Analyse und Statistik */}
          <CategoryRow
            label={t.performanceLabel}
            description={t.performanceDescription}
            enabled={performance}
            onChange={setPerformance}
          />
          <Divider />

          {/* Marketing */}
          <CategoryRow
            label={t.marketingLabel}
            description={t.marketingDescription}
            enabled={marketing}
            onChange={setMarketing}
          />

          {/* Bottom padding inside scroll area */}
          <div style={{ height: 24 }} />
        </div>

        {/* Sticky buttons — always visible, never scrolls away.
             Mobile: stacked (primary on top via flex-col-reverse with DOM order [secondary, primary]).
             md+:    side-by-side, right-aligned (flex-row justify-end). */}
        <div className="flex flex-col-reverse gap-3 px-6 pt-4 pb-8 md:flex-row md:justify-end" style={{ flexShrink: 0 }}>
          {/* Secondary — left on desktop, bottom on mobile */}
          <div className="w-full md:w-auto">
            <CookieButton
              bg={T.secondaryBg}
              bgHover={T.secondaryHoverBg}
              border={T.secondaryBorder}
              borderHover={T.secondaryBorder}
              color={T.textSecondary}
              onClick={() => dismiss(performance, marketing)}
            >
              {t.rejectOptional}
            </CookieButton>
          </div>

          {/* Primary — right on desktop, top on mobile */}
          <div className="w-full md:w-auto">
            <CookieButton
              bg={T.primaryBg}
              bgHover={T.primaryHoverBg}
              border={T.primaryBorder}
              borderHover={T.primaryHoverBorder}
              color={T.text}
              onClick={() => dismiss(true, true)}
            >
              {t.acceptAll}
            </CookieButton>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ── Sub-components ────────────────────────────────────────────────────────── */

function Divider() {
  return <div style={{ borderTop: `1px solid ${T.divider}`, margin: '20px 0' }} />;
}

interface CategoryRowProps {
  label:       string;
  description: string;
  enabled?:    boolean;
  onChange?:   (v: boolean) => void;
}

function CategoryRow({ label, description, enabled, onChange }: CategoryRowProps) {
  const hasToggle = onChange !== undefined;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 8 }}>
        <h3 style={{ margin: 0, color: T.text, fontWeight: 600, fontSize: '1.0625rem', fontFamily: 'inherit' }}>
          {label}
        </h3>

        {hasToggle && (
          <Toggle enabled={!!enabled} onChange={onChange!} />
        )}
      </div>
      <p style={{ margin: 0, color: T.textSecondary, fontSize: '0.875rem', lineHeight: 1.6, fontFamily: 'inherit' }}>
        {description}
      </p>
    </div>
  );
}

interface ToggleProps {
  enabled:  boolean;
  onChange: (v: boolean) => void;
}

function Toggle({ enabled, onChange }: ToggleProps) {
  /* Track dimensions */
  const W = 52; // px, total
  const H = 28;
  const KNOB = 20;
  const GAP  = 3; // px from edge when off

  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={() => onChange(!enabled)}
      style={{
        position:        'relative',
        flexShrink:      0,
        width:           W,
        height:          H,
        borderRadius:    H / 2,
        border:          `1px solid ${T.toggleBorder}`,
        backgroundColor: enabled ? T.toggleOnBg : T.toggleOffBg,
        cursor:          'pointer',
        padding:         0,
        transition:      'background-color 200ms ease',
        fontFamily:      'inherit',
        outline:         'none',
      }}
    >
      {/* Knob — explicit left:0 so transform is predictable */}
      <span
        style={{
          position:        'absolute',
          top:             GAP,
          left:            0,
          width:           KNOB,
          height:          KNOB,
          borderRadius:    '50%',
          backgroundColor: enabled ? T.knobOn : T.knobOff,
          transform:       enabled
            ? `translateX(${W - KNOB - GAP - 2}px)` /* 52-20-3-2(borders)=27 */
            : `translateX(${GAP}px)`,
          transition:      'transform 200ms ease, background-color 200ms ease',
          display:         'block',
          pointerEvents:   'none',
        }}
      />
    </button>
  );
}

/* Generic cookie button with scale(0.98) on press */
interface CookieBtnProps {
  bg:          string;
  bgHover:     string;
  border:      string;
  borderHover: string;
  color:       string;
  onClick:     () => void;
  children:    React.ReactNode;
}

function CookieButton({ bg, bgHover, border, borderHover, color, onClick, children }: CookieBtnProps) {
  const [hovered,  setHovered]  = useState(false);
  const [pressing, setPressing] = useState(false);

  const currentBg     = hovered ? bgHover     : bg;
  const currentBorder = hovered ? borderHover : border;

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPressing(false); }}
      onMouseDown={() => setPressing(true)}
      onMouseUp={() => setPressing(false)}
      onTouchStart={() => setPressing(true)}
      onTouchEnd={() => setPressing(false)}
      style={{
        width:           '100%',
        height:          48,
        borderRadius:    50,
        border:          `1px solid ${currentBorder}`,
        backgroundColor: currentBg,
        color:           color,
        fontSize:        '0.875rem',
        letterSpacing:   '0.06em',
        fontFamily:      'inherit',
        cursor:          'pointer',
        whiteSpace:      'nowrap',
        paddingLeft:     24,
        paddingRight:    24,
        transform:       pressing ? 'scale(0.98)' : 'scale(1)',
        transition:      'background-color 200ms ease, border-color 200ms ease, transform 120ms ease',
      }}
    >
      {children}
    </button>
  );
}
